import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import GroupTypeRoleSelector from './GroupTypeRoleSelector';
import { useGroupTypeNameMap } from '../hooks/useGroupTypeNameMap';
import { GroupPermission } from '../types/group';
import {
  permissionsToSelections,
  selectionsToPermissions,
} from '../utils/groupTypeRoleSelection';

export interface GroupPermissionsFieldProps {
  value: GroupPermission[];
  onChange: (next: GroupPermission[]) => void;
  enableRoleSelection?: boolean;
  requireSelection?: boolean;
  buttonLabel?: string;
  helperText?: string;
  emptyHint?: string;
  title?: string;
}

const GroupPermissionsField: React.FC<GroupPermissionsFieldProps> = ({
  value,
  onChange,
  enableRoleSelection = true,
  requireSelection = false,
  buttonLabel = 'Select group types & roles',
  helperText,
  emptyHint,
  title,
}) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const { groupNameById, scopeByGroupTypeId } =
    useGroupTypeNameMap('permissions-field');

  const initialSelections = useMemo(
    () => permissionsToSelections(value, scopeByGroupTypeId),
    [value, scopeByGroupTypeId]
  );

  const sortedValue = useMemo(
    () =>
      [...value].sort((a, b) =>
        (groupNameById.get(a.group_type_id) || a.group_type_id).localeCompare(
          groupNameById.get(b.group_type_id) || b.group_type_id,
          undefined,
          { sensitivity: 'base' }
        )
      ),
    [value, groupNameById]
  );

  const handleSave = (
    selections: ReturnType<typeof permissionsToSelections>
  ) => {
    onChange(selectionsToPermissions(selections));
  };

  const handleDelete = (groupTypeId: string) => {
    onChange(value.filter((p) => p.group_type_id !== groupTypeId));
  };

  return (
    <Box>
      {helperText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <Button
        variant="outlined"
        onClick={() => setSelectorOpen(true)}
        sx={{ mb: 1 }}
      >
        {buttonLabel}
      </Button>
      {sortedValue.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {sortedValue.map((p) => (
            <Chip
              key={p.group_type_id}
              label={groupNameById.get(p.group_type_id) || p.group_type_id}
              onDelete={() => handleDelete(p.group_type_id)}
              size="small"
            />
          ))}
        </Box>
      )}
      <GroupTypeRoleSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSave={handleSave}
        initialValue={initialSelections}
        title={title ?? buttonLabel}
        enableRoleSelection={enableRoleSelection}
        requireSelection={requireSelection}
        emptySelectionHint={
          emptyHint ??
          (enableRoleSelection
            ? 'Leave empty to allow all signed-in users. Add group types to restrict access.'
            : 'Select one or more group types.')
        }
      />
    </Box>
  );
};

export default GroupPermissionsField;
